import { useEffect, useRef, useState } from 'react';

// The Philosophy film at the top of the home page: muted, looping, no controls, nothing to click.
//
// YouTube picks the quality itself; the player API only lets us ask for 1080p, and it can only
// give it when the film has a 1080p version. If the API does not load, the plain embed below
// plays anyway (at whatever quality YouTube chooses).

let apiPromise = null;

function loadPlayerApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previous?.();
        resolve(window.YT);
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return apiPromise;
}

function HeroFilm({ videoId, title }) {
  const holderRef = useRef(null);
  const [apiFailed, setApiFailed] = useState(false);

  useEffect(() => {
    if (!videoId) return undefined;
    let player = null;
    let alive = true;

    const askForBestQuality = (target) => {
      target.setPlaybackQuality?.('hd1080');
      target.setPlaybackQualityRange?.('hd1080', 'hd1080');
    };

    // cc_load_policy only stops the player forcing subtitles on; a viewer whose YouTube account
    // turns captions on by default still gets them. The captions module also loads after the
    // player is ready, so it has to be thrown away again whenever the modules change and when
    // playback starts.
    const dropCaptions = (target) => {
      try {
        target.unloadModule?.('captions');
        target.unloadModule?.('cc');
        target.setOption?.('captions', 'track', {});
        target.setOption?.('cc', 'track', {});
      } catch {
        // The module is not there yet: the next call catches it.
      }
    };

    loadPlayerApi()
      .then((YT) => {
        if (!alive || !holderRef.current) return;
        player = new YT.Player(holderRef.current, {
          videoId,
          host: 'https://www.youtube.com',
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
            vq: 'hd1080',
          },
          events: {
            onReady: (event) => {
              event.target.mute();
              dropCaptions(event.target);
              askForBestQuality(event.target);
              event.target.playVideo();
            },
            onApiChange: (event) => dropCaptions(event.target),
            onStateChange: (event) => {
              if (event.data === 1) dropCaptions(event.target);
              // Ended: start it over, so the film keeps running without a playlist.
              if (event.data === 0) {
                event.target.seekTo(0, true);
                event.target.playVideo();
              }
            },
            onPlaybackQualityChange: (event) => askForBestQuality(event.target),
            // 101 and 150 mean the film itself is not allowed in embedded players (a setting or
            // a claim on the video); 2 is a bad id and 5 a player problem. Nothing on this side
            // can play a film YouTube blocks, so the code is worth seeing while developing.
            onError: (event) => {
              if (process.env.NODE_ENV !== 'production') {
                console.warn(`HeroFilm: YouTube refused ${videoId} (error ${event.data})`);
              }
            },
          },
        });
      })
      .catch(() => alive && setApiFailed(true));

    return () => {
      alive = false;
      player?.destroy?.();
    };
  }, [videoId]);

  if (apiFailed) {
    const params =
      'autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&vq=hd1080';
    return (
      <div className="hero-film">
        <iframe
          title={title}
          src={`https://www.youtube.com/embed/${videoId}?${params}&playlist=${videoId}`}
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          tabIndex={-1}
        />
      </div>
    );
  }

  return (
    <div className="hero-film">
      <div ref={holderRef} title={title} />
    </div>
  );
}

export default HeroFilm;
